// client/src/components/analytics/PerformancePanel.jsx
//
// "Performance" for an event, or one event item (raffle / auction / contest /
// challenge / ticket item): tracked-link clicks → conversions, top posts,
// revenue by platform. Growth Phase 1, feature_key "analytics_attribution".
//
// Data: GET /api/analytics/:tenant/attribution/performance (server
// services/attribution.js performanceReport). Tables + CSS bars, no chart
// library. Text sits on the .surface card (styles/surfaces.css).

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getJson } from '../../services/http';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { barPct, conversionRate, formatCount, formatMoney } from './attributionFormat';
import './PerformancePanel.css';

const DAY_OPTIONS = [30, 90, 365];

export default function PerformancePanel({ tenantId, eventId, itemId = null, className = '' }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const qs = new URLSearchParams({ days: String(days) });
      if (eventId) qs.set('eventId', String(eventId));
      if (itemId) qs.set('itemId', String(itemId));
      const res = await getJson(`/api/analytics/${Number(tenantId)}/attribution/performance?${qs}`);
      setData(res.data || null);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId, eventId, itemId, days]);

  useEffect(() => { load(); }, [load]);

  const kindLabel = (k) => t(`performance.kinds.${k}`, k);
  const platformLabel = (p) => t(`performance.platforms.${p}`, p);

  let body;
  if (loading && !data) {
    body = <LoadingState inline />;
  } else if (error) {
    body = <ErrorState onRetry={load} message={t('performance.loadError', 'Could not load performance.')} />;
  } else if (!data || !data.available) {
    body = <div className="note info">{t('performance.notAvailable', 'Performance needs a database update before it can show data.')}</div>;
  } else {
    const tot = data.totals || {};
    const kinds = Object.entries(data.conversionsByKind || {}).sort((a, b) => b[1].count - a[1].count);
    const posts = (data.byPost || []).slice(0, 5);
    const platforms = data.byPlatform || [];
    const maxPlatformRevenue = Math.max(0, ...platforms.map((p) => p.revenue));
    const maxPlatformClicks = Math.max(0, ...platforms.map((p) => p.clicks));
    const rate = conversionRate(tot.attributedConversions, tot.clicks);
    const empty = !tot.clicks && !tot.conversions;

    body = (
      <>
        {!data.attributionReady && (
          <div className="note warn">{t('performance.attributionPending', 'Clicks show here now. Crediting conversions to posts starts after a database update.')}</div>
        )}

        <div className="perf-kpis">
          <Kpi label={t('performance.clicks', 'Clicks')} value={formatCount(tot.clicks)} />
          <Kpi label={t('performance.uniqueVisitors', 'Unique visitors')} value={formatCount(tot.uniqueVisitors)} />
          <Kpi
            label={t('performance.conversions', 'Conversions')}
            value={formatCount(tot.conversions)}
            sub={t('performance.fromLinks', '{{count}} from links', { count: tot.attributedConversions || 0 })}
          />
          <Kpi label={t('performance.revenue', 'Revenue')} value={formatMoney(tot.revenue)} />
        </div>

        {empty ? (
          <div className="note info">{t('performance.empty', 'No tracked clicks or conversions in this period yet. Links in scheduled posts are tracked automatically.')}</div>
        ) : (
          <>
            <div className="perf-funnel" aria-label={t('performance.funnel', 'Clicks to conversions')}>
              <div className="perf-funnel-row">
                <span>{t('performance.clicks', 'Clicks')}</span>
                <div className="perf-bar"><div className="perf-bar-fill" style={{ width: tot.clicks ? '100%' : '0%' }} /></div>
                <span className="perf-num">{formatCount(tot.clicks)}</span>
              </div>
              <div className="perf-funnel-row">
                <span>{t('performance.fromLinksShort', 'Conversions from links')}</span>
                <div className="perf-bar"><div className="perf-bar-fill alt" style={{ width: `${barPct(tot.attributedConversions, tot.clicks)}%` }} /></div>
                <span className="perf-num">{formatCount(tot.attributedConversions)}</span>
              </div>
              {rate != null && (
                <p className="surface-muted perf-small">{t('performance.rate', '{{rate}} conversions per 100 clicks', { rate })}</p>
              )}
            </div>

            {kinds.length > 0 && (
              <>
                <h5>{t('performance.byKind', 'Conversions by type')}</h5>
                <table className="perf-table">
                  <thead>
                    <tr>
                      <th>{t('performance.type', 'Type')}</th>
                      <th className="num">{t('performance.count', 'Count')}</th>
                      <th className="num">{t('performance.value', 'Value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kinds.map(([k, v]) => (
                      <tr key={k}>
                        <td>{kindLabel(k)}</td>
                        <td className="num">{formatCount(v.count)}</td>
                        <td className="num">{formatMoney(v.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <h5>{t('performance.topPosts', 'Top posts')}</h5>
            {posts.length === 0 ? (
              <p className="surface-muted perf-small">{t('performance.noPosts', 'No clicks on post links yet.')}</p>
            ) : (
              <table className="perf-table">
                <thead>
                  <tr>
                    <th>{t('performance.post', 'Post')}</th>
                    <th className="num">{t('performance.clicks', 'Clicks')}</th>
                    <th className="num">{t('performance.conversions', 'Conversions')}</th>
                    <th className="num">{t('performance.revenue', 'Revenue')}</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => (
                    <tr key={p.postId}>
                      <td className="perf-post">{p.content || t('performance.untitledPost', 'Post #{{id}}', { id: p.postId })}</td>
                      <td className="num">{formatCount(p.clicks)}</td>
                      <td className="num">{formatCount(p.conversions)}</td>
                      <td className="num">{formatMoney(p.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {platforms.length > 0 && (
              <>
                <h5>{maxPlatformRevenue > 0 ? t('performance.revenueByPlatform', 'Revenue by platform') : t('performance.clicksByPlatform', 'Clicks by platform')}</h5>
                <ul className="perf-bars">
                  {platforms.map((p) => {
                    const useRevenue = maxPlatformRevenue > 0;
                    return (
                      <li key={p.platform}>
                        <div className="perf-bar-label">
                          <span>{platformLabel(p.platform)}</span>
                          <span className="perf-num">
                            {useRevenue ? formatMoney(p.revenue) : formatCount(p.clicks)}
                            {' · '}
                            {t('performance.convShort', '{{count}} conv.', { count: p.conversions })}
                          </span>
                        </div>
                        <div className="perf-bar">
                          <div
                            className="perf-bar-fill"
                            style={{ width: `${barPct(useRevenue ? p.revenue : p.clicks, useRevenue ? maxPlatformRevenue : maxPlatformClicks)}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}

        <p className="surface-muted perf-small">
          {t('performance.footnote', 'A conversion is credited to the last tracked link the visitor clicked in the {{days}} days before it. Bots are excluded.', { days: data.lookbackDays || 45 })}
        </p>
      </>
    );
  }

  return (
    <section className={`surface perf-panel ${className}`} data-testid="performance-panel">
      <div className="perf-head">
        <h4>{t('performance.title', 'Performance')}</h4>
        <label className="perf-days">
          <span className="sr-only">{t('performance.period', 'Period')}</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t('performance.period', 'Period')}>
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>{t('performance.lastDays', 'Last {{days}} days', { days: d })}</option>
            ))}
          </select>
        </label>
      </div>
      {body}
    </section>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="perf-kpi">
      <div className="perf-kpi-value">{value}</div>
      <div className="perf-kpi-label">{label}</div>
      {sub && <div className="perf-kpi-sub">{sub}</div>}
    </div>
  );
}
